import React, { useState, useEffect, useCallback, useContext, useRef, useMemo } from 'react';
import { Marker, Popup, Circle } from 'react-leaflet';
import { Link } from 'react-router-dom';
import ChasseHuntMap, { createDivIcon, DataAttribution, L } from '../components/ChasseHuntMap';
import Header from '../components/Header';
import api from '../services/api';
import { LangContext } from '../App';

// ── Constants ─────────────────────────────────────────────────────────────────

const QC_CENTER = [52, -72];
const QC_ZOOM = 6;
const GEO_ZOOM = 10;

const ESPECES_LISTE = [
  { val: 'orignal', fr: 'Orignal', en: 'Moose' },
  { val: 'chevreuil', fr: 'Chevreuil', en: 'White-tailed deer' },
  { val: 'ours', fr: 'Ours noir', en: 'Black bear' },
  { val: 'canards', fr: 'Canards', en: 'Ducks' },
  { val: 'outardes', fr: 'Outardes', en: 'Canada geese' },
  { val: 'perdrix', fr: 'Perdrix', en: 'Ruffed grouse' },
  { val: 'lièvre', fr: 'Lièvre', en: 'Snowshoe hare' },
  { val: 'coyote', fr: 'Coyote', en: 'Coyote' },
];

const PROVINCES_LISTE = [
  { code: 'QC', fr: 'Québec', en: 'Quebec' },
  { code: 'ON', fr: 'Ontario', en: 'Ontario' },
  { code: 'AB', fr: 'Alberta', en: 'Alberta' },
  { code: 'BC', fr: 'Colombie-Britannique', en: 'British Columbia' },
  { code: 'MB', fr: 'Manitoba', en: 'Manitoba' },
  { code: 'NB', fr: 'Nouveau-Brunswick', en: 'New Brunswick' },
  { code: 'NL', fr: 'Terre-Neuve', en: 'Newfoundland' },
  { code: 'NS', fr: 'Nouvelle-Écosse', en: 'Nova Scotia' },
  { code: 'SK', fr: 'Saskatchewan', en: 'Saskatchewan' },
];

const FILTRE_VIDE = { province: '', species: '', capacite: '', mode: '' };

// ── Translations ──────────────────────────────────────────────────────────────

const T = {
  fr: {
    title: 'Terres disponibles',
    vueCarte: 'Carte',
    vueListe: 'Liste',
    province: 'Province',
    species: 'Espèce',
    capacite: 'Chasseurs min.',
    mode: 'Mode',
    enchère: 'Enchère',
    prixFixe: 'Prix fixe',
    tousModes: 'Tous modes',
    toutesProvinces: 'Toutes',
    toutesEspeces: 'Toutes les espèces',
    presDeM: '📍 Près de moi',
    geoChargement: 'Localisation…',
    geoErreur: 'GPS non available ou refusé',
    voir: 'Voir les annonces →',
    ha: 'ha',
    chasseurs: 'chasseurs max',
    aucunResultat: 'Aucune terre trouvée avec ces filtres.',
    chargement: 'Chargement…',
    erreur: 'Erreur lors du chargement des terres.',
    total: (n) => `${n} terre${n > 1 ? 's' : ''} trouvée${n > 1 ? 's' : ''}`,
    rayon: (r) => `Dans un rayon de ${r} km`,
    precedent: '← Précédent',
    suivant: 'Suivant →',
    pageSur: (p, t) => `Page ${p} / ${t}`,
    superficie: 'Superficie',
    especes: 'Espèces',
    capaciteLabel: 'Capacité',
    reinitFiltres: 'Réinitialiser',
    afficherZones: 'Zones de chasse',
    masquerZones: 'Masquer zones',
    zoneLabel: 'Zone de chasse',
    reglements: 'Règlements applicables',
    aucuneZone: 'Hors zone réglementée',
    aucunReglement: 'Aucun règlement répertorié pour cette zone.',
    zonesChargement: 'Chargement des zones…',
    voirMoins: 'Voir moins',
    datesVariables: 'Dates variables',
    catGrandGibier: 'Grand gibier',
    catPetitGibier: 'Petit gibier',
    catSauvagine: 'Sauvagine',
    catAutre: 'Autres espèces',
    noteFederale:
      'Règlements fédéraux — Service canadien de la faune. Saisons et limites fixées annuellement par le gouvernement du Canada, distinctes des règlements provinciaux ci-dessus.',
    imprimer: 'Imprimer',
    disclaimerQc:
      'Information à titre indicatif. Seule la réglementation officielle du MELCCFP (Québec) ou du Service canadien de la faune (sauvagine, fédéral) fait foi pour la pratique de la chasse.',
    disclaimerOn:
      'Information à titre indicatif. Seule la réglementation officielle du Ministry of Natural Resources (Ontario) ou du Service canadien de la faune (sauvagine, fédéral) fait foi pour la pratique de la chasse.',
    copyrightQc: '© Gouvernement du Québec',
    copyrightOn: "© King's Printer for Ontario",
  },
  en: {
    title: 'Available lands',
    vueCarte: 'Map',
    vueListe: 'List',
    province: 'Province',
    species: 'Species',
    capacite: 'Min. hunters',
    mode: 'Mode',
    enchère: 'Auction',
    prixFixe: 'Fixed price',
    tousModes: 'All modes',
    toutesProvinces: 'All',
    toutesEspeces: 'All species',
    presDeM: '📍 Near me',
    geoChargement: 'Locating…',
    geoErreur: 'GPS unavailable or denied',
    voir: 'View listings →',
    ha: 'ha',
    chasseurs: 'hunters max',
    aucunResultat: 'No lands found with these filters.',
    chargement: 'Loading…',
    erreur: 'Error loading lands.',
    total: (n) => `${n} land${n > 1 ? 's' : ''} found`,
    rayon: (r) => `Within a ${r} km radius`,
    precedent: '← Previous',
    suivant: 'Next →',
    pageSur: (p, t) => `Page ${p} / ${t}`,
    superficie: 'Area',
    especes: 'Species',
    capaciteLabel: 'Capacity',
    reinitFiltres: 'Reset',
    afficherZones: 'Hunting zones',
    masquerZones: 'Hide zones',
    zoneLabel: 'Hunting zone',
    reglements: 'Applicable regulations',
    aucuneZone: 'Outside regulated zone',
    aucunReglement: 'No regulations registered for this zone.',
    zonesChargement: 'Loading zones…',
    catGrandGibier: 'Big game',
    catPetitGibier: 'Small game',
    catSauvagine: 'Waterfowl',
    catAutre: 'Other species',
    noteFederale:
      'Federal regulations — Canadian Wildlife Service. Seasons and bag limits set yearly by the Government of Canada, separate from the provincial rules listed above.',
    imprimer: 'Print',
    disclaimerQc:
      'For informational purposes only. Only the official MELCCFP regulations (Quebec) or Canadian Wildlife Service rules (waterfowl, federal) are authoritative for actual hunting practice.',
    disclaimerOn:
      'For informational purposes only. Only the official Ministry of Natural Resources regulations (Ontario) or Canadian Wildlife Service rules (waterfowl, federal) are authoritative for actual hunting practice.',
    copyrightQc: '© Gouvernement du Québec',
    copyrightOn: "© King's Printer for Ontario",
    voirMoins: 'See less',
    datesVariables: 'Variable dates',
  },
};

// ── Leaflet icons ─────────────────────────────────────────────────────────────

const ICON_TERRE = createDivIcon({
  html: `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="32" viewBox="0 0 22 32">
    <path d="M11 0C4.9 0 0 4.9 0 11c0 8.3 11 21 11 21s11-12.7 11-21C22 4.9 17.1 0 11 0z"
      fill="#1e6b3a" stroke="#c9a227" stroke-width="1.5"/>
    <circle cx="11" cy="11" r="4.5" fill="#c9a227" opacity="0.9"/>
  </svg>`,
  iconSize: [22, 32],
  iconAnchor: [11, 32],
});

const ICON_POSITION = createDivIcon({
  html: `<div style="width:14px;height:14px;background:#4a90d9;border:3px solid #fff;border-radius:50%;box-shadow:0 0 0 5px rgba(74,144,217,0.2)"></div>`,
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

// Icône custom pour les clusters — `L` est re-exporté par ChasseHuntMap
// pour éviter d'importer `leaflet` directement dans la page.
function clusterIconCreate(cluster) {
  return L.divIcon({
    className: '',
    html: `<div style="
      width:36px;height:36px;
      background:rgba(30,107,58,0.85);
      border:2px solid var(--couleur-or);
      border-radius:50%;
      display:flex;align-items:center;justify-content:center;
      color:var(--couleur-or);font-weight:700;font-size:0.8rem;
      box-shadow:0 2px 8px rgba(0,0,0,0.5);
    ">${cluster.getChildCount()}</div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
  });
}

// ── Badge espèce ──────────────────────────────────────────────────────────────

function BadgeEspece({ species, lang }) {
  const info = ESPECES_LISTE.find((e) => e.val === species);
  const label = info ? info[lang] : species;
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: '0.68rem',
        background: 'rgba(201,162,39,0.18)',
        border: '1px solid rgba(201,162,39,0.75)',
        color: '#e0b830',
        borderRadius: '20px',
        padding: '1px 8px',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

// ── Carte terre (vue liste) ───────────────────────────────────────────────────

function CarteTerreItem({ terre, t, lang }) {
  const especes = terre.available_species ?? [];
  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: '10px',
        padding: '1.2rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.65rem',
      }}
    >
      <div>
        <h3
          style={{
            margin: 0,
            fontFamily: 'Georgia, serif',
            fontSize: '1rem',
            color: 'var(--couleur-texte)',
            lineHeight: 1.3,
          }}
        >
          {terre.title}
        </h3>
        <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.4)', marginTop: '3px' }}>
          {terre.province}
          {terre.address && ` · ${terre.address}`}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          gap: '1.2rem',
          fontSize: '0.8rem',
          color: 'rgba(255,255,255,0.6)',
        }}
      >
        <span>
          📐 {terre.area_hectares} {t.ha}
        </span>
        <span>
          👥 {terre.max_hunters} {t.chasseurs}
        </span>
        {terre.distance_km != null && <span>📍 {terre.distance_km} km</span>}
      </div>

      {especes.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
          {especes.slice(0, 5).map((e) => (
            <BadgeEspece key={e} species={e} lang={lang} />
          ))}
          {especes.length > 5 && (
            <span
              style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.3)', padding: '1px 6px' }}
            >
              +{especes.length - 5}
            </span>
          )}
        </div>
      )}

      <Link
        to={`/lands/${terre.id}`}
        style={{
          alignSelf: 'flex-start',
          marginTop: 'auto',
          fontSize: '0.8rem',
          color: 'var(--couleur-or)',
          textDecoration: 'none',
          fontWeight: 600,
        }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.8')}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
      >
        {t.voir}
      </Link>
    </div>
  );
}

// ── Popup carte (HTML statique rendu par Leaflet) ─────────────────────────────

// Contenu du popup (sans le wrapper <Popup>) — <ChasseHuntMap> ajoute le
// wrapper de son côté. Garder un composant React pour le content (pas du
// HTML brut) garantit que les liens internes restent gérés par le router.
function PopupCarteContent({ p, lang, t }) {
  const especes = p.available_species ?? [];
  const badges = especes
    .slice(0, 4)
    .map((e) => {
      const info = ESPECES_LISTE.find((x) => x.val === e);
      return `<span style="display:inline-block;font-size:0.65rem;background:rgba(201,162,39,0.18);border:1px solid rgba(201,162,39,0.75);color:#c9a227;border-radius:20px;padding:1px 7px;margin:1px 2px 1px 0">${info ? info[lang] : e}</span>`;
    })
    .join('');

  return (
    <div
      style={{
        fontFamily: 'system-ui, sans-serif',
        color: 'var(--couleur-fond)',
        lineHeight: 1.4,
      }}
    >
      <div
        style={{
          fontWeight: 700,
          fontSize: '0.9rem',
          marginBottom: '4px',
          color: 'var(--couleur-fond-carte)',
        }}
      >
        {p.title}
      </div>
      <div style={{ fontSize: '0.75rem', color: '#555', marginBottom: '6px' }}>
        {p.province} · {p.area_hectares} {t.ha} · {p.max_hunters} {t.chasseurs}
      </div>
      {badges && (
        <div style={{ marginBottom: '8px' }} dangerouslySetInnerHTML={{ __html: badges }} />
      )}
      <a
        href={`/lands/${p.id}`}
        style={{
          display: 'inline-block',
          fontSize: '0.78rem',
          background: 'var(--couleur-vert-accent)',
          color: '#fff',
          borderRadius: '5px',
          padding: '4px 12px',
          textDecoration: 'none',
          fontWeight: 600,
        }}
      >
        {t.voir}
      </a>
    </div>
  );
}

// ── Barre de filtres ──────────────────────────────────────────────────────────

const selectStyle = {
  background: 'rgba(255,255,255,0.07)',
  border: '1px solid rgba(255,255,255,0.16)',
  borderRadius: '6px',
  color: 'var(--couleur-texte)',
  padding: '0.45rem 0.75rem',
  fontSize: '0.82rem',
  outline: 'none',
  cursor: 'pointer',
  minWidth: '120px',
};

const selectPanelStyle = {
  background: '#1a2e1a',
  border: '1px solid rgba(255,255,255,0.16)',
  borderRadius: '6px',
  color: '#f5f0e8',
  padding: '0.45rem 0.75rem',
  fontSize: '0.82rem',
  outline: 'none',
  cursor: 'pointer',
  width: '100%',
};

function BarreFiltres({ filtres, setFiltres, geoChargement, geoMsg, onPresDeM, t, lang }) {
  const filtresActifs = Object.values(filtres).some((v) => v !== '');

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.6rem',
        alignItems: 'center',
        padding: '0.8rem 1.2rem',
        background: 'rgba(255,255,255,0.03)',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
      }}
    >
      {/* Province */}
      <select
        value={filtres.province}
        onChange={(e) => setFiltres((f) => ({ ...f, province: e.target.value }))}
        style={selectStyle}
        onFocus={(e) => (e.target.style.borderColor = 'rgba(201,162,39,0.5)')}
        onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.16)')}
      >
        <option value="">{t.toutesProvinces}</option>
        {PROVINCES_LISTE.map((p) => (
          <option key={p.code} value={p.code}>
            {p[lang]} ({p.code})
          </option>
        ))}
      </select>

      {/* Espèce */}
      <select
        value={filtres.species}
        onChange={(e) => setFiltres((f) => ({ ...f, species: e.target.value }))}
        style={selectStyle}
        onFocus={(e) => (e.target.style.borderColor = 'rgba(201,162,39,0.5)')}
        onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.16)')}
      >
        <option value="">{t.toutesEspeces}</option>
        {ESPECES_LISTE.map((e) => (
          <option key={e.val} value={e.val}>
            {e[lang]}
          </option>
        ))}
      </select>

      {/* Capacité min */}
      <input
        type="number"
        min={1}
        max={20}
        value={filtres.capacite}
        onChange={(e) => setFiltres((f) => ({ ...f, capacite: e.target.value }))}
        placeholder={t.capacite}
        style={{ ...selectStyle, width: '110px', minWidth: 'unset' }}
        onFocus={(e) => (e.target.style.borderColor = 'rgba(201,162,39,0.5)')}
        onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.16)')}
      />

      {/* Mode */}
      <select
        value={filtres.mode}
        onChange={(e) => setFiltres((f) => ({ ...f, mode: e.target.value }))}
        style={selectStyle}
        onFocus={(e) => (e.target.style.borderColor = 'rgba(201,162,39,0.5)')}
        onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.16)')}
      >
        <option value="">{t.tousModes}</option>
        <option value="enchere">{t.enchère}</option>
        <option value="fixed_price">{t.prixFixe}</option>
      </select>

      {/* Réinitialiser */}
      {filtresActifs && (
        <button
          onClick={() => setFiltres(FILTRE_VIDE)}
          style={{
            background: 'transparent',
            border: '1px solid rgba(255,255,255,0.18)',
            color: 'rgba(255,255,255,0.45)',
            borderRadius: '6px',
            padding: '0.45rem 0.8rem',
            fontSize: '0.78rem',
            cursor: 'pointer',
          }}
        >
          ✕ {t.reinitFiltres}
        </button>
      )}

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* Près de moi */}
      {geoMsg && (
        <span
          style={{
            fontSize: '0.76rem',
            color:
              geoMsg.includes('non') || geoMsg.includes('unavail')
                ? 'var(--couleur-texte-erreur)'
                : 'rgba(255,255,255,0.4)',
          }}
        >
          {geoMsg}
        </span>
      )}
      <button
        onClick={onPresDeM}
        disabled={geoChargement}
        style={{
          background: 'rgba(74,144,217,0.12)',
          border: '1px solid rgba(74,144,217,0.35)',
          color: '#7ab8e8',
          borderRadius: '6px',
          padding: '0.45rem 1rem',
          fontSize: '0.82rem',
          cursor: geoChargement ? 'not-allowed' : 'pointer',
          fontWeight: 500,
          whiteSpace: 'nowrap',
          opacity: geoChargement ? 0.6 : 1,
        }}
      >
        {geoChargement ? t.geoChargement : t.presDeM}
      </button>
    </div>
  );
}

// ── Formatage date courte ─────────────────────────────────────────────────────

const MOIS_FR = [
  'janv.',
  'févr.',
  'mars',
  'avr.',
  'mai',
  'juin',
  'juil.',
  'août',
  'sept.',
  'oct.',
  'nov.',
  'déc.',
];
const MOIS_EN = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

function formatDateCourte(dateStr, lang) {
  if (!dateStr) return null;
  const s = String(dateStr).split('T')[0];
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  const mois = lang === 'fr' ? MOIS_FR[m - 1] : MOIS_EN[m - 1];
  return lang === 'fr' ? `${d} ${mois} ${y}` : `${mois} ${d}, ${y}`;
}

// ── Style des polygones de zones ──────────────────────────────────────────────

const zoneStyle = {
  color: 'var(--couleur-or)',
  weight: 1.5,
  opacity: 0.65,
  fillColor: '#1e6b3a',
  fillOpacity: 0.08,
};

// ── Popup de zone — construction HTML ────────────────────────────────────────
//
// Leaflet rend le popup à partir d'une string HTML (bindPopup/setPopupContent).
// On compose donc le contenu ici plutôt qu'en JSX. Toutes les valeurs
// dynamiques venant de la BD passent par escapeHtml — défense en profondeur
// même si la source est de confiance (MELCCFP, MNRF).

function escapeHtml(value) {
  if (value === null || value === undefined) return '';
  return String(value).replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]
  );
}

const POPUP_STYLE = {
  wrap: 'min-width:260px;max-width:340px;font-family:inherit;',
  code: 'font-weight:700;font-size:0.95rem;color:#c9a227;',
  name: 'font-size:0.78rem;color:rgba(0,0,0,0.6);margin-top:2px;',
  province:
    'font-size:0.7rem;color:rgba(0,0,0,0.5);margin-top:4px;text-transform:uppercase;letter-spacing:0.05em;',
  categoryGroup: 'margin-top:8px;',
  categoryHeader:
    'font-size:0.78rem;font-weight:600;color:rgba(0,0,0,0.78);margin:6px 0 4px;display:flex;align-items:center;gap:6px;',
  // details/summary natifs : on supprime le marker par défaut et on ajoute
  // notre propre chevron rotatif via le pseudo-élément ::before — voir le
  // bloc <style> injecté plus bas (cf. styleSheetBlock).
  detailsBlock: 'margin-top:6px;padding-top:6px;border-top:1px solid rgba(0,0,0,0.06);',
  detailsSummary:
    'cursor:pointer;font-size:0.78rem;font-weight:600;color:rgba(0,0,0,0.7);list-style:none;outline:none;user-select:none;',
  ruleItem:
    'font-size:0.78rem;color:rgba(0,0,0,0.85);margin-bottom:4px;padding-left:8px;border-left:2px solid #c9a227;',
  ruleSpecies: 'font-weight:600;line-height:1.25;',
  ruleMeta: 'font-size:0.7rem;color:rgba(0,0,0,0.55);margin-top:1px;',
  empty: 'font-size:0.74rem;color:rgba(0,0,0,0.45);font-style:italic;margin-top:6px;',
  federalNotice:
    'font-size:0.7rem;color:rgba(140,80,0,0.95);background:rgba(255,200,80,0.12);' +
    'border-left:3px solid rgba(200,120,0,0.7);padding:5px 7px;margin:6px 0;line-height:1.35;',
  disclaimer:
    'font-size:0.68rem;color:rgba(0,0,0,0.6);background:rgba(0,0,0,0.04);' +
    'border-left:3px solid rgba(0,0,0,0.25);padding:6px 8px;margin-top:10px;line-height:1.4;',
  printRow:
    'display:flex;justify-content:flex-end;margin-top:8px;padding-top:6px;border-top:1px solid rgba(0,0,0,0.06);',
  printBtn:
    'background:#1e6b3a;color:#fff;border:none;border-radius:4px;padding:5px 12px;' +
    'font-size:0.75rem;font-weight:600;cursor:pointer;font-family:inherit;',
  copyright:
    'font-size:0.65rem;color:rgba(0,0,0,0.45);margin-top:6px;padding-top:6px;' +
    'border-top:1px solid rgba(0,0,0,0.1);font-style:italic;',
};

// Métadonnées d'affichage par catégorie d'espèce. L'ordre des clés dicte
// l'ordre d'apparition dans la popup. Le tri DB de fn_zone_lookup matche
// déjà cet ordre, mais on re-trie ici pour être robuste à un changement.
//
// SEUL le grand gibier est ouvert par défaut — c'est la priorité du
// chasseur quand il regarde une zone. Les autres catégories sont en
// <details> collapsé pour ne pas surcharger visuellement la popup et
// donner un retour produit clair (le chasseur clique ce qu'il veut voir).
const CATEGORY_ORDER = ['grand_gibier', 'petit_gibier', 'sauvagine', 'autre'];
const CATEGORY_META = {
  grand_gibier: { icon: '🦌', tKey: 'catGrandGibier', collapsible: false, federal: false },
  petit_gibier: { icon: '🦃', tKey: 'catPetitGibier', collapsible: true, federal: false },
  // Sauvagine = oiseaux migrateurs, règlement fédéral (Service canadien de
  // la faune). Encart explicatif pour bien séparer du provincial.
  sauvagine: { icon: '🦆', tKey: 'catSauvagine', collapsible: true, federal: true },
  autre: { icon: '📋', tKey: 'catAutre', collapsible: true, federal: false },
};

function buildRuleItem(r, t, lang) {
  const debut = formatDateCourte(r.start_date, lang);
  const fin = formatDateCourte(r.end_date, lang);
  const dates = debut ? (fin ? `${debut} → ${fin}` : debut) : t.datesVariables;
  const metas = [r.weapon_type, r.season_type, dates].filter(Boolean).join(' · ');
  return (
    `<div style="${POPUP_STYLE.ruleItem}">` +
    `<div style="${POPUP_STYLE.ruleSpecies}">${escapeHtml(r.species_fr || '—')}</div>` +
    `<div style="${POPUP_STYLE.ruleMeta}">${escapeHtml(metas)}</div>` +
    `</div>`
  );
}

function buildCategorySection(cat, rules, t, lang) {
  const meta = CATEGORY_META[cat] || CATEGORY_META.autre;
  const label = t[meta.tKey] || cat;
  const header = `${meta.icon} ${escapeHtml(label)} (${rules.length})`;
  const rulesHtml = rules.map((r) => buildRuleItem(r, t, lang)).join('');
  const federalNotice = meta.federal
    ? `<div style="${POPUP_STYLE.federalNotice}">⚠ ${escapeHtml(t.noteFederale)}</div>`
    : '';

  if (meta.collapsible) {
    return (
      `<details style="${POPUP_STYLE.detailsBlock}">` +
      `<summary style="${POPUP_STYLE.detailsSummary}">${header}</summary>` +
      federalNotice +
      rulesHtml +
      `</details>`
    );
  }
  return (
    `<div style="${POPUP_STYLE.categoryGroup}">` +
    `<div style="${POPUP_STYLE.categoryHeader}">${header}</div>` +
    rulesHtml +
    `</div>`
  );
}

function buildZonePopupHtml(zoneInfo, t, lang) {
  if (!zoneInfo || !zoneInfo.zone_code) {
    return (
      `<div style="${POPUP_STYLE.wrap}">` +
      `<div style="${POPUP_STYLE.empty}">${escapeHtml(t.aucuneZone)}</div>` +
      `</div>`
    );
  }

  // species_fr null = ligne « zone existe mais aucune règle joint via
  // zone_regle_mapping ». On filtre — l'utilisateur verra juste l'header
  // zone + le message « aucun règlement » si vide.
  const regles = (zoneInfo.reglements || []).filter((r) => r.species_fr);

  // Groupement par catégorie. Les rangées sans category remontée vont dans
  // 'autre' par défaut (rétro-compatibilité avec d'anciennes données).
  const byCat = { grand_gibier: [], petit_gibier: [], sauvagine: [], autre: [] };
  for (const r of regles) {
    const cat = r.category && byCat[r.category] !== undefined ? r.category : 'autre';
    byCat[cat].push(r);
  }

  const sections = CATEGORY_ORDER.filter((cat) => byCat[cat].length > 0)
    .map((cat) => buildCategorySection(cat, byCat[cat], t, lang))
    .join('');

  const body =
    sections || `<div style="${POPUP_STYLE.empty}">${escapeHtml(t.aucunReglement)}</div>`;

  // Référence légale par province. Le copyright n'est affiché que si
  // l'attribution correspondante existe (QC ou ON) — d'autres provinces
  // futures pourraient ne pas en avoir.
  const copyrightTexte =
    zoneInfo.province === 'QC' ? t.copyrightQc : zoneInfo.province === 'ON' ? t.copyrightOn : null;
  const copyrightLine = copyrightTexte
    ? `<div style="${POPUP_STYLE.copyright}">${escapeHtml(copyrightTexte)}</div>`
    : '';

  // Disclaimer adapté à la province : mention du bon ministère (MELCCFP
  // pour QC, Ministry of Natural Resources pour ON). Fallback QC sinon.
  const disclaimerTexte = zoneInfo.province === 'ON' ? t.disclaimerOn : t.disclaimerQc;
  const disclaimer = `<div style="${POPUP_STYLE.disclaimer}">⚠ ${escapeHtml(disclaimerTexte)}</div>`;
  const printRow =
    `<div style="${POPUP_STYLE.printRow}" data-no-print="1">` +
    `<button type="button" style="${POPUP_STYLE.printBtn}" ` +
    `data-print-zone="1">🖨 ${escapeHtml(t.imprimer)}</button>` +
    `</div>`;

  return (
    // data-zone-content="1" sert d'ancre fiable pour printZoneFromPopupEl
    // qui cible ce wrapper plutôt que de naviguer le DOM Leaflet (où
    // firstElementChild renvoie .leaflet-popup-content-wrapper, pas le
    // notre).
    `<div data-zone-content="1" style="${POPUP_STYLE.wrap}">` +
    `<div style="${POPUP_STYLE.code}">${escapeHtml(zoneInfo.zone_code)}</div>` +
    (zoneInfo.zone_name
      ? `<div style="${POPUP_STYLE.name}">${escapeHtml(zoneInfo.zone_name)}</div>`
      : '') +
    (zoneInfo.province
      ? `<div style="${POPUP_STYLE.province}">${escapeHtml(zoneInfo.province)}</div>`
      : '') +
    body +
    disclaimer +
    printRow +
    copyrightLine +
    `</div>`
  );
}

// Imprime le contenu d'une popup zone dans une fenêtre dédiée.
//
// On clone le DOM, on retire les éléments data-no-print="1" et on force
// tous les <details> en open=true. On RETIRE aussi l'attribut style du
// wrapper racine (qui avait min-width:260px;max-width:340px;font-size:
// inherit; — adaptés à un popup Leaflet, désastreux sur une feuille
// imprimée). Le print stylesheet applique ensuite des tailles correctes
// pour une page A4/Letter.
function printZoneFromPopupEl(popupEl) {
  if (!popupEl) return;
  // popupEl est le wrapper .leaflet-popup complet. Notre vraie zone de
  // contenu vit plusieurs niveaux plus bas et porte data-zone-content="1".
  // On cible direct sans s'occuper du chrome Leaflet.
  const sourceEl = popupEl.querySelector('[data-zone-content="1"]');
  if (!sourceEl) return;
  const clone = sourceEl.cloneNode(true);
  clone.querySelectorAll('[data-no-print="1"]').forEach((el) => el.remove());
  clone.querySelectorAll('details').forEach((d) => d.setAttribute('open', ''));
  // Strip l'attribut style du wrapper qu'on imprime (il avait
  // min-width:260px;max-width:340px adapté à Leaflet, désastreux sur A4).
  clone.removeAttribute('style');

  const win = window.open('', '_blank', 'width=820,height=900');
  if (!win) return; // popup blocker — l'utilisateur verra l'icône bloquée

  const dateStr = new Date().toLocaleString('fr-CA');
  const css = `
    @page { size: letter; margin: 1.5cm; }
    body {
      font-family: Georgia, serif;
      color: #111;
      margin: 0;
      padding: 0;
      font-size: 11pt;
      line-height: 1.45;
    }
    /* Le wrapper hérite des dimensions de la page maintenant que son
       style inline a été retiré. Les enfants gardent leurs styles inline
       (couleurs, paddings) mais on relâche les tailles min/max au cas où. */
    .print-content > div { min-width: 0 !important; max-width: none !important; }
    .print-content { padding: 4mm 0; }
    /* Le zone_code en or peu lisible à l'impression — on force noir gras */
    .print-content > div > div:first-child {
      color: #000 !important;
      font-size: 18pt !important;
      font-weight: bold !important;
      margin-bottom: 4px;
    }
    /* Le zone_name juste sous */
    .print-content > div > div:nth-child(2) {
      font-size: 12pt !important;
      color: #333 !important;
      margin-bottom: 8px;
    }
    /* Catégorie headers */
    .print-content [style*="font-weight:600"] {
      font-size: 12pt !important;
      margin-top: 12px !important;
    }
    /* Items de règles */
    .print-content [style*="border-left:2px solid"] {
      font-size: 10.5pt !important;
      padding: 4px 0 4px 10px !important;
      margin-bottom: 4px !important;
      page-break-inside: avoid;
    }
    summary { cursor: default; list-style: none; outline: none; }
    summary::-webkit-details-marker { display: none; }
    details[open] > summary::before { content: ''; }
    .print-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-bottom: 8px;
      border-bottom: 2px solid #1e6b3a;
      margin-bottom: 12px;
    }
    .print-header .brand { font-weight: bold; font-size: 14pt; color: #1e6b3a; }
    .print-header .date { font-size: 9pt; color: #666; }
    .print-footer {
      margin-top: 24px;
      font-size: 9pt;
      color: #666;
      border-top: 1px solid #ccc;
      padding-top: 8px;
      text-align: center;
    }
  `;
  win.document.write(
    `<!DOCTYPE html><html lang="fr"><head><meta charset="utf-8">` +
      `<title>ChasseHunt — Règlements de zone</title>` +
      `<style>${css}</style></head><body>` +
      `<div class="print-header">` +
      `<span class="brand">ChasseHunt</span>` +
      `<span class="date">${escapeHtml(dateStr)}</span>` +
      `</div>` +
      `<div class="print-content">${clone.outerHTML}</div>` +
      `<div class="print-footer">Imprimé depuis chassehunt.ca · ` +
      `Information à titre indicatif — voir réglementation officielle MELCCFP / SCF</div>` +
      `</body></html>`
  );
  win.document.close();
  // Petit délai pour laisser le navigateur appliquer le CSS avant l'ouverture
  // du dialogue d'impression.
  setTimeout(() => {
    try {
      win.focus();
      win.print();
    } catch {
      /* ignore — le user peut imprimer manuellement depuis Ctrl+P */
    }
  }, 150);
}

// ── Composant principal ───────────────────────────────────────────────────────

export default function TerresDisponibles() {
  const { lang } = useContext(LangContext);
  const t = T[lang];

  // Vue
  const [vue, setVue] = useState('carte');

  // Filtres
  const [filtres, setFiltres] = useState(FILTRE_VIDE);
  const [filtreOuvert, setFiltreOuvert] = useState(false);
  const filtreRef = useRef(null);

  useEffect(() => {
    if (!filtreOuvert) return;
    const handler = (e) => {
      if (filtreRef.current && !filtreRef.current.contains(e.target)) {
        setFiltreOuvert(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [filtreOuvert]);

  // Géolocalisation
  const [geoPos, setGeoPos] = useState(null);
  const [geoChargement, setGeoChargement] = useState(false);
  const [geoMsg, setGeoMsg] = useState('');
  const [centreCarte, setCentreCarte] = useState(null);

  // Vue liste
  const [lands, setTerres] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState('');

  // Vue carte — lands
  const [features, setFeatures] = useState([]);
  const [chargementCarte, setChargementCarte] = useState(false);

  // Markers prêts pour <ChasseHuntMap>. On mémoïse pour ne pas casser
  // l'identité quand le parent re-render sans nouvelles features (Leaflet
  // re-monte ses Markers à chaque changement de référence du tableau, ce
  // qui ferme les popups ouverts).
  const terreMarkers = useMemo(
    () =>
      features
        .filter((f) => f.geometry?.coordinates)
        .map((feat) => {
          const [lng, lat] = feat.geometry.coordinates;
          const p = feat.properties;
          return {
            id: p.id,
            lat,
            lng,
            icon: ICON_TERRE,
            popup: <PopupCarteContent p={p} lang={lang} t={t} />,
          };
        }),
    [features, lang, t]
  );

  // Vue carte — zones de chasse
  const [zonesGeoJson, setZonesGeoJson] = useState(null);
  const [afficherZones, setAfficherZones] = useState(false);
  const [chargementZones, setChargementZones] = useState(false);
  const [zoneInfo, setZoneInfo] = useState(null);
  const [reglExpand, setReglExpand] = useState(false);

  // ── Chargement vue liste ───────────────────────────────────────────────────

  useEffect(() => {
    if (vue !== 'liste') return;
    let annulee = false;
    setChargement(true);
    setErreur('');

    const params = { page, limite: 20 };
    if (filtres.province) params.province = filtres.province;
    if (filtres.species) params.species = filtres.species;
    if (filtres.capacite) params.capacite = filtres.capacite;

    api
      .get('/lands', { params })
      .then((res) => {
        if (annulee) return;
        setTerres(res.data.lands ?? []);
        setPages(res.data.pages ?? 1);
        setTotal(res.data.total ?? 0);
      })
      .catch(() => {
        if (!annulee) setErreur(t.erreur);
      })
      .finally(() => {
        if (!annulee) setChargement(false);
      });

    return () => {
      annulee = true;
    };
  }, [vue, filtres, page]);

  // Reset page on filter change
  useEffect(() => {
    setPage(1);
  }, [filtres]);

  // ── Chargement vue carte — lands ──────────────────────────────────────────

  const chargerCarte = useCallback(
    async (geo = null) => {
      setChargementCarte(true);
      try {
        let data;
        const filterParams = {};
        if (filtres.province) filterParams.province = filtres.province;
        if (filtres.species) filterParams.species = filtres.species;
        if (filtres.capacite) filterParams.capacite = filtres.capacite;

        if (geo) {
          const res = await api.get('/geo/lands', {
            params: { lat: geo.lat, lng: geo.lng, rayon: 150, ...filterParams },
          });
          data = (res.data.data ?? []).map((t) => ({
            type: 'Feature',
            geometry: t.localisation,
            properties: {
              id: t.id,
              title: t.title,
              province: t.province,
              area_hectares: t.area_hectares,
              available_species: t.available_species,
              max_hunters: t.max_hunters,
              distance_km: t.distance_km,
            },
          }));
        } else {
          const res = await api.get('/geo/carte', { params: filterParams });
          data = res.data.features ?? [];
        }
        setFeatures(data);
      } catch {
        setFeatures([]);
      } finally {
        setChargementCarte(false);
      }
    },
    [filtres]
  );

  useEffect(() => {
    if (vue === 'carte') chargerCarte(geoPos);
  }, [vue, geoPos, filtres, chargerCarte]);

  // ── Chargement zones de chasse ─────────────────────────────────────────────
  //
  // L'ancien endpoint /api/v1/geo/zones renvoyait ~13.5 MB de polygones
  // pleine résolution via fn_zones_featurecollection — trop lourd pour un
  // chargement sur la page principale. On utilise désormais le nouvel
  // endpoint /api/v1/zones/geojson?province= qui applique
  // ST_SimplifyPreserveTopology(0.005) et cache 1h en mémoire backend.
  // Deux appels en parallèle (QC + ON) puis fusion des FeatureCollection.

  // Ref de protection contre les appels concurrents — évite que la fonction
  // se ré-instancie au moindre changement de zonesGeoJson (ce qui faisait
  // re-déclencher le useEffect de préchargement plusieurs fois et causait
  // un flicker visible sur les boutons « Filtres » et « Zones de chasse »).
  const chargementZonesRef = useRef(false);

  const chargerZones = useCallback(async () => {
    // Double protection : refuse si une charge est en cours OU si on a déjà
    // les données. Sans cette ref, la fonction se recréait à chaque setState
    // (zonesGeoJson, chargementZones) et le useEffect plus bas pouvait la
    // ré-appeler avant que la première charge ne finisse.
    if (chargementZonesRef.current) return;
    chargementZonesRef.current = true;
    setChargementZones(true);
    try {
      const [rQc, rOn] = await Promise.all([
        api.get('/zones/geojson', { params: { province: 'QC' } }),
        api.get('/zones/geojson', { params: { province: 'ON' } }),
      ]);
      const merge = {
        type: 'FeatureCollection',
        features: [...(rQc.data.features || []), ...(rOn.data.features || [])],
      };
      setZonesGeoJson(merge);
    } catch {
      setZonesGeoJson(null);
    } finally {
      setChargementZones(false);
      chargementZonesRef.current = false;
    }
  }, []);

  const chercherZone = useCallback(async (lat, lng) => {
    try {
      const res = await api.get('/geo/zones/lookup', { params: { lat, lng } });
      setZoneInfo(res.data);
    } catch {
      setZoneInfo(null);
    }
  }, []);

  // Lookup zone dès que la position GPS change (déclaré après chercherZone)
  useEffect(() => {
    if (geoPos) chercherZone(geoPos.lat, geoPos.lng);
  }, [geoPos, chercherZone]);

  // Réduire le panneau quand la zone change
  useEffect(() => {
    setReglExpand(false);
  }, [zoneInfo]);

  // Préchargement des zones dès qu'on est sur la vue carte. Évite le bug
  // « il faut cliquer 2 fois » : sans préchargement, le 1er clic flippait
  // afficherZones=true alors que zonesGeoJson était encore null (fetch en
  // cours) → aucun polygone visible → l'utilisateur clique à nouveau pour
  // « relancer » et ferme la couche.
  //
  // Deps minimales [vue] — chargerZones est maintenant stable (useCallback
  // vide grâce à la ref), et la guard interne du callback rejette les
  // doubles appels. Sans cette restriction, l'effet se déclenchait à
  // chaque setState (chargementZones, zonesGeoJson) et causait du flicker
  // sur les boutons de la barre.
  useEffect(() => {
    if (vue === 'carte') {
      chargerZones();
    }
  }, [vue, chargerZones]);

  const toggleZones = () => {
    // Si le premier chargement est en cours, on IGNORE le clic. Sans ça,
    // l'utilisateur qui re-clique pendant les 2-3 s de fetch initial
    // ferme le toggle (afficherZones=true → false) avant que les données
    // n'arrivent — résultat perçu : « il faut cliquer plusieurs fois ».
    // Le bouton est aussi rendu disabled pendant cette fenêtre pour
    // donner un feedback visuel.
    if (chargementZones && !zonesGeoJson) return;
    if (!zonesGeoJson) chargerZones();
    setAfficherZones((v) => !v);
  };

  // ── Géolocalisation ────────────────────────────────────────────────────────

  const handlePresDeM = () => {
    if (!navigator.geolocation) {
      setGeoMsg(t.geoErreur);
      return;
    }
    setGeoChargement(true);
    setGeoMsg('');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setGeoPos({ lat: latitude, lng: longitude });
        setCentreCarte([latitude, longitude]);
        setGeoMsg(t.rayon(150));
        setGeoChargement(false);
      },
      () => {
        setGeoMsg(t.geoErreur);
        setGeoChargement(false);
      },
      { timeout: 10000 }
    );
  };

  // ── Styles ─────────────────────────────────────────────────────────────────

  const btnVueStyle = (active) => ({
    background: active ? 'rgba(201,162,39,0.18)' : 'transparent',
    border: `1px solid ${active ? 'rgba(201,162,39,0.55)' : 'rgba(255,255,255,0.15)'}`,
    color: active ? 'var(--couleur-or)' : 'rgba(255,255,255,0.5)',
    padding: '0.4rem 1.2rem',
    borderRadius: '6px',
    fontSize: '0.85rem',
    fontWeight: active ? 600 : 400,
    cursor: 'pointer',
    transition: 'all 0.15s',
  });

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--couleur-fond)',
        color: 'var(--couleur-texte)',
      }}
    >
      <Header />

      {/* ── Barre title + toggle de vue ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '1rem 2rem',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          flexWrap: 'wrap',
          gap: '0.8rem',
        }}
      >
        <h1
          style={{
            fontFamily: 'Georgia, serif',
            fontSize: '1.35rem',
            color: 'var(--couleur-texte)',
            margin: 0,
          }}
        >
          🏡 {t.title}
        </h1>
        {/* Switcher Carte / Liste */}
        <div
          style={{
            display: 'flex',
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: '8px',
            padding: '3px',
            gap: 0,
          }}
        >
          {[
            { key: 'carte', label: t.vueCarte, icon: '🗺️' },
            { key: 'liste', label: t.vueListe, icon: '📋' },
          ].map(({ key, label, icon }) => (
            <button
              key={key}
              onClick={() => {
                setVue(key);
                setFiltreOuvert(false);
              }}
              style={{
                background: vue === key ? 'rgba(201,162,39,0.22)' : 'transparent',
                border: 'none',
                borderRadius: '6px',
                color: vue === key ? 'var(--couleur-or)' : 'rgba(255,255,255,0.45)',
                padding: '0.35rem 1.1rem',
                fontSize: '0.85rem',
                fontWeight: vue === key ? 600 : 400,
                cursor: 'pointer',
                transition: 'all 0.15s',
                whiteSpace: 'nowrap',
              }}
            >
              {icon} {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Vue carte ── */}
      {vue === 'carte' && (
        <div style={{ position: 'relative' }}>
          {/* Indicateur chargement lands */}
          {chargementCarte && (
            <div
              style={{
                position: 'absolute',
                top: '12px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 500,
                background: 'rgba(6,14,6,0.9)',
                border: '1px solid rgba(201,162,39,0.3)',
                borderRadius: '6px',
                padding: '0.4rem 1rem',
                fontSize: '0.8rem',
                color: 'var(--couleur-or)',
                pointerEvents: 'none',
              }}
            >
              {t.chargement}
            </div>
          )}

          {/* Boutons top-right : filtre + zones de chasse */}
          <div
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              zIndex: 500,
              display: 'flex',
              gap: '0.5rem',
              alignItems: 'flex-start',
            }}
          >
            {/* ── Bouton filtre ── */}
            <div style={{ position: 'relative' }} ref={filtreRef}>
              <button
                onClick={() => setFiltreOuvert((o) => !o)}
                title={lang === 'fr' ? 'Filtres' : 'Filters'}
                style={{
                  background:
                    filtreOuvert || Object.values(filtres).some((v) => v !== '')
                      ? 'rgba(201,162,39,0.2)'
                      : 'rgba(6,14,6,0.88)',
                  border: `1px solid ${filtreOuvert || Object.values(filtres).some((v) => v !== '') ? 'rgba(201,162,39,0.6)' : 'rgba(255,255,255,0.2)'}`,
                  color:
                    filtreOuvert || Object.values(filtres).some((v) => v !== '')
                      ? 'var(--couleur-or)'
                      : 'rgba(255,255,255,0.55)',
                  borderRadius: '6px',
                  padding: '0.4rem 0.85rem',
                  fontSize: '0.78rem',
                  cursor: 'pointer',
                  fontWeight:
                    filtreOuvert || Object.values(filtres).some((v) => v !== '') ? 600 : 400,
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                >
                  <line x1="2" y1="4" x2="14" y2="4" />
                  <circle cx="5" cy="4" r="1.5" fill="currentColor" stroke="none" />
                  <line x1="2" y1="8" x2="14" y2="8" />
                  <circle cx="10" cy="8" r="1.5" fill="currentColor" stroke="none" />
                  <line x1="2" y1="12" x2="14" y2="12" />
                  <circle cx="6" cy="12" r="1.5" fill="currentColor" stroke="none" />
                </svg>
                {lang === 'fr' ? 'Filtres' : 'Filters'}
                {Object.values(filtres).some((v) => v !== '') && ' •'}
              </button>

              {/* Panneau filtre flottant */}
              {filtreOuvert && (
                <div
                  style={{
                    position: 'absolute',
                    top: '40px',
                    right: 0,
                    background: 'rgba(10,22,10,0.97)',
                    border: '1px solid rgba(201,162,39,0.25)',
                    borderRadius: '10px',
                    padding: '1rem',
                    width: '260px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.6rem',
                  }}
                >
                  <div
                    style={{
                      fontSize: '0.75rem',
                      color: 'rgba(255,255,255,0.4)',
                      marginBottom: '0.2rem',
                      fontWeight: 600,
                      letterSpacing: '0.05em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {lang === 'fr' ? 'Filtres' : 'Filters'}
                  </div>
                  <select
                    value={filtres.province}
                    onChange={(e) => setFiltres((f) => ({ ...f, province: e.target.value }))}
                    style={selectPanelStyle}
                  >
                    <option value="">{t.toutesProvinces}</option>
                    {PROVINCES_LISTE.map((p) => (
                      <option key={p.code} value={p.code}>
                        {p[lang]} ({p.code})
                      </option>
                    ))}
                  </select>
                  <select
                    value={filtres.species}
                    onChange={(e) => setFiltres((f) => ({ ...f, species: e.target.value }))}
                    style={selectPanelStyle}
                  >
                    <option value="">{t.toutesEspeces}</option>
                    {ESPECES_LISTE.map((e) => (
                      <option key={e.val} value={e.val}>
                        {e[lang]}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    min={1}
                    max={20}
                    value={filtres.capacite}
                    onChange={(e) => setFiltres((f) => ({ ...f, capacite: e.target.value }))}
                    placeholder={t.capacite}
                    style={{ ...selectPanelStyle, background: '#1a2e1a' }}
                  />
                  <select
                    value={filtres.mode}
                    onChange={(e) => setFiltres((f) => ({ ...f, mode: e.target.value }))}
                    style={selectPanelStyle}
                  >
                    <option value="">{t.tousModes}</option>
                    <option value="enchere">{t.enchère}</option>
                    <option value="fixed_price">{t.prixFixe}</option>
                  </select>
                  {Object.values(filtres).some((v) => v !== '') && (
                    <button
                      onClick={() => setFiltres(FILTRE_VIDE)}
                      style={{
                        background: 'transparent',
                        border: '1px solid rgba(255,255,255,0.15)',
                        color: 'rgba(255,255,255,0.4)',
                        borderRadius: '6px',
                        padding: '0.4rem',
                        fontSize: '0.78rem',
                        cursor: 'pointer',
                      }}
                    >
                      ✕ {t.reinitFiltres}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* ── Bouton zones de chasse ── */}
            <button
              onClick={toggleZones}
              disabled={chargementZones && !zonesGeoJson}
              style={{
                background: afficherZones ? 'rgba(201,162,39,0.2)' : 'rgba(6,14,6,0.88)',
                border: `1px solid ${afficherZones ? 'rgba(201,162,39,0.6)' : 'rgba(255,255,255,0.2)'}`,
                color: afficherZones ? 'var(--couleur-or)' : 'rgba(255,255,255,0.55)',
                borderRadius: '6px',
                padding: '0.4rem 0.85rem',
                fontSize: '0.78rem',
                cursor: chargementZones && !zonesGeoJson ? 'wait' : 'pointer',
                opacity: chargementZones && !zonesGeoJson ? 0.6 : 1,
                fontWeight: afficherZones ? 600 : 400,
                whiteSpace: 'nowrap',
              }}
            >
              🗺 {afficherZones ? t.masquerZones : t.afficherZones}
              {chargementZones && ' …'}
            </button>
          </div>

          {/* GeoJSON zones + cluster custom passent par les props standards ;
              le marker GPS + Circle (overlay non-marker-array) passent par
              `children`. flyToCenter recadre quand un user clique « près
              de moi ». La prop `geoJSON` ne rend que `zonesGeoJson` quand
              afficherZones est true — sinon undefined. */}
          <ChasseHuntMap
            center={centreCarte ?? QC_CENTER}
            zoom={centreCarte ? GEO_ZOOM : QC_ZOOM}
            height="calc(100vh - 170px)"
            basemap="osm"
            flyToCenter={!!centreCarte}
            geoJSON={afficherZones ? zonesGeoJson : undefined}
            geoJSONOptions={{
              key: 'zones-layer',
              style: zoneStyle,
              onEachFeature: (feature, layer) => {
                const p = feature.properties;
                if (!p?.zone_code) return;

                // Tooltip de survol — léger, pas de fetch
                layer.bindTooltip(p.zone_name ? `${p.zone_code} — ${p.zone_name}` : p.zone_code, {
                  sticky: true,
                  direction: 'auto',
                  opacity: 0.9,
                });

                // Popup au clic — fetch des règlements à la demande puis
                // remplacement du contenu. Le placeholder "chargement"
                // évite un saut visuel pendant l'appel HTTP.
                // Attache l'event click sur le bouton « Imprimer » du popup
                // après chaque setPopupContent (le bouton fait partie du
                // HTML rendu par buildZonePopupHtml, repéré via
                // data-print-zone="1"). On évite un inline onclick qui peut
                // être bloqué par notre CSP.
                const attacherBoutonImprimer = () => {
                  const el = layer.getPopup()?.getElement();
                  const btn = el?.querySelector('[data-print-zone="1"]');
                  if (btn) {
                    btn.onclick = (ev) => {
                      ev.stopPropagation();
                      printZoneFromPopupEl(el);
                    };
                  }
                };

                layer.on('click', async (e) => {
                  const { lat, lng } = e.latlng;
                  const loadingHtml =
                    `<div style="${POPUP_STYLE.wrap}"><div style="${POPUP_STYLE.empty}">` +
                    `${escapeHtml(t.zonesChargement)}</div></div>`;
                  layer
                    .bindPopup(loadingHtml, { maxWidth: 340, minWidth: 240 })
                    .openPopup(e.latlng);
                  try {
                    const res = await api.get('/geo/zones/lookup', { params: { lat, lng } });
                    layer.setPopupContent(buildZonePopupHtml(res.data, t, lang));
                    attacherBoutonImprimer();
                  } catch {
                    layer.setPopupContent(
                      `<div style="${POPUP_STYLE.wrap}"><div style="${POPUP_STYLE.empty}">` +
                        `${escapeHtml(t.erreur)}</div></div>`
                    );
                  }
                });
              },
            }}
            cluster
            clusterOptions={{
              chunkedLoading: true,
              maxClusterRadius: 60,
              iconCreateFunction: clusterIconCreate,
            }}
            markers={terreMarkers}
          >
            {/* Géolocalisation utilisateur : Circle 150 km + marker bleu.
                Escape hatch children parce que c'est un overlay non-marker
                (et qu'on ne veut pas le clusteriser avec les terres). */}
            {geoPos && (
              <>
                <Marker position={[geoPos.lat, geoPos.lng]} icon={ICON_POSITION} />
                <Circle
                  center={[geoPos.lat, geoPos.lng]}
                  radius={150000}
                  pathOptions={{
                    color: 'rgba(74,144,217,0.6)',
                    fillColor: 'rgba(74,144,217,0.06)',
                    fillOpacity: 1,
                    weight: 1,
                  }}
                />
              </>
            )}
            {/* Mention obligatoire MELCCFP clause 6 — n'apparaît dans
                l'attribution control que pendant que la couche zones est
                visible (se monte/démonte avec afficherZones). */}
            {afficherZones && (
              <DataAttribution text="Zones QC : &copy; Gouvernement du Qu&eacute;bec" />
            )}
          </ChasseHuntMap>

          {/* ── Bouton GPS "près de moi" — bas-droite ── */}
          <div
            style={{ position: 'absolute', bottom: '24px', right: '12px', zIndex: 500 }}
            className="geo-btn-wrap"
          >
            <button
              onClick={handlePresDeM}
              disabled={geoChargement}
              style={{
                background: geoPos ? 'rgba(74,144,217,0.25)' : 'rgba(6,14,6,0.88)',
                border: `1px solid ${geoPos ? 'rgba(74,144,217,0.7)' : 'rgba(255,255,255,0.2)'}`,
                color: geoPos ? '#7ab8e8' : 'rgba(255,255,255,0.7)',
                borderRadius: '8px',
                width: '40px',
                height: '40px',
                fontSize: '1.1rem',
                cursor: geoChargement ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: geoChargement ? 0.6 : 1,
                boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
              }}
            >
              {geoChargement ? '…' : '📍'}
            </button>
            {/* Tooltip — apparaît au survol via CSS */}
            <div
              style={{
                position: 'absolute',
                bottom: '46px',
                right: 0,
                background: 'rgba(10,22,10,0.95)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: '6px',
                padding: '0.3rem 0.6rem',
                fontSize: '0.72rem',
                color: 'rgba(255,255,255,0.7)',
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
                opacity: 0,
                transition: 'opacity 0.15s',
              }}
              className="geo-tooltip"
            >
              {lang === 'fr' ? 'Près de moi' : 'Near me'}
              {geoMsg && (
                <div
                  style={{
                    color:
                      geoMsg.includes('non') || geoMsg.includes('unavail')
                        ? '#e57373'
                        : 'rgba(255,255,255,0.4)',
                    marginTop: '2px',
                  }}
                >
                  {geoMsg}
                </div>
              )}
            </div>
          </div>

          {/* Compteur lands */}
          {features.length > 0 && (
            <div
              style={{
                position: 'fixed',
                bottom: '24px',
                left: '12px',
                zIndex: 2000,
                background: 'rgba(6,14,6,0.88)',
                border: '1px solid rgba(201,162,39,0.3)',
                borderRadius: '6px',
                padding: '0.35rem 0.9rem',
                fontSize: '0.78rem',
                color: 'rgba(255,255,255,0.6)',
                pointerEvents: 'none',
              }}
            >
              🏡 {t.total(features.length)}
              {geoPos && ` · ${t.rayon(150)}`}
            </div>
          )}

          {/* Panneau info zone (affiché après GPS lookup) */}
          {zoneInfo && (
            <div
              style={{
                position: 'fixed',
                bottom: '24px',
                right: '12px',
                zIndex: 2000,
                background: 'rgba(6,14,6,0.92)',
                border: '1px solid rgba(201,162,39,0.4)',
                borderRadius: '8px',
                padding: '0.75rem 1rem',
                maxWidth: reglExpand ? '360px' : '240px',
                width: reglExpand ? '360px' : 'auto',
                fontSize: '0.8rem',
                transition: 'max-width 0.2s ease, width 0.2s ease',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}
              >
                <div>
                  <div style={{ fontWeight: 700, color: 'var(--couleur-or)', marginBottom: '2px' }}>
                    {t.zoneLabel}
                  </div>
                  {zoneInfo.zone_code ? (
                    <div
                      style={{
                        fontSize: '1rem',
                        fontWeight: 700,
                        color: 'var(--couleur-texte)',
                        lineHeight: 1.1,
                      }}
                    >
                      {zoneInfo.zone_code}
                    </div>
                  ) : (
                    <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}>
                      {t.aucuneZone}
                    </div>
                  )}
                  {zoneInfo.zone_name && (
                    <div
                      style={{
                        color: 'rgba(255,255,255,0.5)',
                        fontSize: '0.72rem',
                        marginTop: '2px',
                      }}
                    >
                      {zoneInfo.zone_name}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setZoneInfo(null)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'rgba(255,255,255,0.3)',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    padding: '0 0 0 8px',
                    lineHeight: 1,
                  }}
                >
                  ✕
                </button>
              </div>

              {zoneInfo.reglements?.length > 0 && (
                <div
                  style={{
                    marginTop: '8px',
                    borderTop: '1px solid rgba(255,255,255,0.08)',
                    paddingTop: '8px',
                  }}
                >
                  <div
                    style={{
                      color: 'rgba(255,255,255,0.35)',
                      fontSize: '0.7rem',
                      marginBottom: '6px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                    }}
                  >
                    {t.reglements}
                  </div>

                  {/* Vue réduite */}
                  {!reglExpand && (
                    <>
                      {zoneInfo.reglements.slice(0, 5).map((r, i) => (
                        <div
                          key={i}
                          style={{
                            color: 'var(--couleur-texte)',
                            fontSize: '0.75rem',
                            marginBottom: '3px',
                            display: 'flex',
                            gap: '6px',
                          }}
                        >
                          <span style={{ color: 'var(--couleur-texte-succes)', flexShrink: 0 }}>
                            ·
                          </span>
                          <span>
                            {r.species_fr}
                            {r.season_type && (
                              <span style={{ color: 'rgba(255,255,255,0.4)' }}>
                                {' '}
                                ({r.season_type})
                              </span>
                            )}
                          </span>
                        </div>
                      ))}
                      {zoneInfo.reglements.length > 5 && (
                        <button
                          onClick={() => setReglExpand(true)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'var(--couleur-or)',
                            cursor: 'pointer',
                            fontSize: '0.72rem',
                            padding: '3px 0 0',
                            textDecoration: 'underline',
                            textUnderlineOffset: '2px',
                          }}
                        >
                          +{zoneInfo.reglements.length - 5} {lang === 'fr' ? 'autres' : 'more'}
                        </button>
                      )}
                    </>
                  )}

                  {/* Vue développée */}
                  {reglExpand && (
                    <>
                      <div style={{ maxHeight: '340px', overflowY: 'auto', paddingRight: '2px' }}>
                        {zoneInfo.reglements.map((r, i) => {
                          const debut = formatDateCourte(r.start_date, lang);
                          const fin = formatDateCourte(r.end_date, lang);
                          return (
                            <div
                              key={i}
                              style={{
                                marginBottom: '8px',
                                paddingBottom: '7px',
                                borderBottom: '1px solid rgba(255,255,255,0.06)',
                              }}
                            >
                              <div style={{ display: 'flex', gap: '6px', alignItems: 'baseline' }}>
                                <span
                                  style={{ color: 'var(--couleur-texte-succes)', flexShrink: 0 }}
                                >
                                  ·
                                </span>
                                <span
                                  style={{
                                    color: 'var(--couleur-texte)',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                  }}
                                >
                                  {r.species_fr}
                                </span>
                              </div>
                              {r.weapon_type && (
                                <div
                                  style={{
                                    marginLeft: '14px',
                                    fontSize: '0.7rem',
                                    color: 'rgba(255,255,255,0.45)',
                                    marginTop: '2px',
                                  }}
                                >
                                  {r.weapon_type}
                                </div>
                              )}
                              <div
                                style={{
                                  marginLeft: '14px',
                                  fontSize: '0.7rem',
                                  color: 'rgba(201,162,39,0.8)',
                                  marginTop: '2px',
                                }}
                              >
                                {debut ? (fin ? `${debut} → ${fin}` : debut) : t.datesVariables}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <button
                        onClick={() => setReglExpand(false)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: 'rgba(255,255,255,0.4)',
                          cursor: 'pointer',
                          fontSize: '0.7rem',
                          padding: '4px 0 0',
                          textDecoration: 'underline',
                          textUnderlineOffset: '2px',
                        }}
                      >
                        {t.voirMoins}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Vue liste — barre filtres + GPS ── */}
      {vue === 'liste' && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '0.5rem',
            padding: '0.65rem 2rem',
            borderBottom: '1px solid rgba(255,255,255,0.07)',
          }}
        >
          {/* ── Bouton filtre ── */}
          <div style={{ position: 'relative' }} ref={filtreRef}>
            <button
              onClick={() => setFiltreOuvert((o) => !o)}
              style={{
                background:
                  filtreOuvert || Object.values(filtres).some((v) => v !== '')
                    ? 'rgba(201,162,39,0.18)'
                    : 'transparent',
                border: `1px solid ${filtreOuvert || Object.values(filtres).some((v) => v !== '') ? 'rgba(201,162,39,0.55)' : 'rgba(255,255,255,0.15)'}`,
                color:
                  filtreOuvert || Object.values(filtres).some((v) => v !== '')
                    ? 'var(--couleur-or)'
                    : 'rgba(255,255,255,0.5)',
                borderRadius: '6px',
                padding: '0.4rem 0.85rem',
                fontSize: '0.82rem',
                fontWeight:
                  filtreOuvert || Object.values(filtres).some((v) => v !== '') ? 600 : 400,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              >
                <line x1="2" y1="4" x2="14" y2="4" />
                <circle cx="5" cy="4" r="1.5" fill="currentColor" stroke="none" />
                <line x1="2" y1="8" x2="14" y2="8" />
                <circle cx="10" cy="8" r="1.5" fill="currentColor" stroke="none" />
                <line x1="2" y1="12" x2="14" y2="12" />
                <circle cx="6" cy="12" r="1.5" fill="currentColor" stroke="none" />
              </svg>
              {lang === 'fr' ? 'Filtres' : 'Filters'}
              {Object.values(filtres).some((v) => v !== '') && ' •'}
            </button>

            {/* Panneau filtre flottant — ouvre à gauche */}
            {filtreOuvert && (
              <div
                style={{
                  position: 'absolute',
                  top: '40px',
                  right: 0,
                  background: 'rgba(10,22,10,0.97)',
                  border: '1px solid rgba(201,162,39,0.25)',
                  borderRadius: '10px',
                  padding: '1rem',
                  width: '260px',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.6rem',
                  zIndex: 100,
                }}
              >
                <div
                  style={{
                    fontSize: '0.75rem',
                    color: 'rgba(255,255,255,0.4)',
                    fontWeight: 600,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                  }}
                >
                  {lang === 'fr' ? 'Filtres' : 'Filters'}
                </div>
                <select
                  value={filtres.province}
                  onChange={(e) => setFiltres((f) => ({ ...f, province: e.target.value }))}
                  style={selectPanelStyle}
                >
                  <option value="">{t.toutesProvinces}</option>
                  {PROVINCES_LISTE.map((p) => (
                    <option key={p.code} value={p.code}>
                      {p[lang]} ({p.code})
                    </option>
                  ))}
                </select>
                <select
                  value={filtres.species}
                  onChange={(e) => setFiltres((f) => ({ ...f, species: e.target.value }))}
                  style={selectPanelStyle}
                >
                  <option value="">{t.toutesEspeces}</option>
                  {ESPECES_LISTE.map((e) => (
                    <option key={e.val} value={e.val}>
                      {e[lang]}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={filtres.capacite}
                  onChange={(e) => setFiltres((f) => ({ ...f, capacite: e.target.value }))}
                  placeholder={t.capacite}
                  style={{ ...selectPanelStyle, background: '#1a2e1a' }}
                />
                <select
                  value={filtres.mode}
                  onChange={(e) => setFiltres((f) => ({ ...f, mode: e.target.value }))}
                  style={selectPanelStyle}
                >
                  <option value="">{t.tousModes}</option>
                  <option value="enchere">{t.enchère}</option>
                  <option value="fixed_price">{t.prixFixe}</option>
                </select>
                {Object.values(filtres).some((v) => v !== '') && (
                  <button
                    onClick={() => {
                      setFiltres(FILTRE_VIDE);
                      setFiltreOuvert(false);
                    }}
                    style={{
                      background: 'transparent',
                      border: '1px solid rgba(255,255,255,0.15)',
                      color: 'rgba(255,255,255,0.4)',
                      borderRadius: '6px',
                      padding: '0.4rem',
                      fontSize: '0.78rem',
                      cursor: 'pointer',
                    }}
                  >
                    ✕ {t.reinitFiltres}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* ── Bouton GPS icône seule avec tooltip ── */}
          <div style={{ position: 'relative' }} className="geo-btn-wrap">
            <button
              onClick={handlePresDeM}
              disabled={geoChargement}
              title={lang === 'fr' ? 'Près de moi' : 'Near me'}
              style={{
                background: geoPos ? 'rgba(74,144,217,0.25)' : 'transparent',
                border: `1px solid ${geoPos ? 'rgba(74,144,217,0.7)' : 'rgba(255,255,255,0.15)'}`,
                color: geoPos ? '#7ab8e8' : 'rgba(255,255,255,0.5)',
                borderRadius: '6px',
                width: '34px',
                height: '34px',
                fontSize: '1rem',
                cursor: geoChargement ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: geoChargement ? 0.6 : 1,
              }}
            >
              {geoChargement ? '…' : '📍'}
            </button>
            <div
              style={{
                position: 'absolute',
                bottom: '40px',
                right: 0,
                background: 'rgba(10,22,10,0.95)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: '6px',
                padding: '0.3rem 0.6rem',
                fontSize: '0.72rem',
                color: 'rgba(255,255,255,0.7)',
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
                opacity: 0,
                transition: 'opacity 0.15s',
                zIndex: 10,
              }}
              className="geo-tooltip"
            >
              {lang === 'fr' ? 'Près de moi' : 'Near me'}
              {geoMsg && (
                <div
                  style={{
                    color:
                      geoMsg.includes('non') || geoMsg.includes('unavail')
                        ? '#e57373'
                        : 'rgba(255,255,255,0.4)',
                    marginTop: '2px',
                  }}
                >
                  {geoMsg}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Vue liste ── */}
      {vue === 'liste' && (
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '1.5rem 1.5rem 4rem' }}>
          {/* Résumé */}
          {!chargement && !erreur && (
            <div
              style={{
                fontSize: '0.78rem',
                color: 'rgba(255,255,255,0.35)',
                marginBottom: '1.2rem',
              }}
            >
              {t.total(total)}
              {geoPos && ` · ${t.rayon(50)}`}
            </div>
          )}

          {/* États */}
          {chargement && (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'rgba(255,255,255,0.3)' }}>
              {t.chargement}
            </div>
          )}
          {erreur && (
            <div
              style={{
                background: 'rgba(200,50,50,0.1)',
                border: '1px solid rgba(200,50,50,0.3)',
                borderRadius: '8px',
                padding: '1rem 1.2rem',
                color: 'var(--couleur-texte-erreur)',
                fontSize: '0.85rem',
              }}
            >
              {erreur}
            </div>
          )}
          {!chargement && !erreur && lands.length === 0 && (
            <div
              style={{
                textAlign: 'center',
                padding: '3rem',
                color: 'rgba(255,255,255,0.3)',
                fontSize: '0.9rem',
              }}
            >
              🌲 {t.aucunResultat}
            </div>
          )}

          {/* Grille */}
          {!chargement && lands.length > 0 && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: '1rem',
                marginBottom: '2rem',
              }}
            >
              {lands.map((terre) => (
                <CarteTerreItem key={terre.id} terre={terre} t={t} lang={lang} />
              ))}
            </div>
          )}

          {/* Pagination */}
          {pages > 1 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '1rem',
                marginTop: '1rem',
              }}
            >
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.18)',
                  color: page <= 1 ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.65)',
                  padding: '0.45rem 1rem',
                  borderRadius: '6px',
                  fontSize: '0.82rem',
                  cursor: page <= 1 ? 'not-allowed' : 'pointer',
                }}
              >
                {t.precedent}
              </button>

              <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)' }}>
                {t.pageSur(page, pages)}
              </span>

              <button
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
                disabled={page >= pages}
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.18)',
                  color: page >= pages ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.65)',
                  padding: '0.45rem 1rem',
                  borderRadius: '6px',
                  fontSize: '0.82rem',
                  cursor: page >= pages ? 'not-allowed' : 'pointer',
                }}
              >
                {t.suivant}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
